import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import CrudListPage from "../../../components/crud/CrudListPage";
import DataTable, { formatCurrency } from "../../../components/crud/DataTable";
import DetailPanel, { PanelSection, PanelRow, SummaryCard, SummaryRow } from "../../../components/v4/DetailPanel";
import { FiCreditCard, FiEdit2 } from "react-icons/fi";

const AccountBalance = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedItem, setSelectedItem] = useState(null);

	useEffect(() => {
		const fetchMonthlyData = async () => {
			try {
				const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/money/AccountBalance/`);
				setData(res.data);
			} catch (err) {
				console.log(err);
			} finally {
				setLoading(false);
			}
		};
		fetchMonthlyData();
	}, []);

	const handleDelete = async (row) => {
		if (!window.confirm(`Delete balance for ${row.account}?`)) return;

		try {
			await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/money/accountBalance/${row.id}`);
			setData((prev) => prev.filter((item) => item.id !== row.id));
			if (selectedItem?.id === row.id) {
				setSelectedItem(null);
			}
		} catch (err) {
			console.log(err);
		}
	};

	const handleRowClick = (row) => {
		setSelectedItem(row);
	};

	// Render panel content
	const renderPanelContent = () => {
		if (!selectedItem) return null;

		return (
			<PanelSection title="Balance Details">
				<PanelRow label="Account" value={selectedItem.account} />
				<PanelRow label="Value" value={formatCurrency(selectedItem.value)} />
				<PanelRow label="Last Updated" value={formatDateTime(selectedItem.date_edited)} />
			</PanelSection>
		);
	};

	// Render panel sidebar
	const renderPanelSidebar = () => {
		if (!selectedItem) return null;

		return (
			<SummaryCard label="Balance">
				<SummaryRow label="Current" value={formatCurrency(selectedItem.value)} />
			</SummaryCard>
		);
	};

	// Panel footer
	const renderPanelFooter = () => {
		if (!selectedItem) return null;
		return (
			<Link to={`/updateAccountBalance/${selectedItem.id}`} className="btn btn--primary">
				<FiEdit2 style={{ marginRight: 6 }} />
				Edit
			</Link>
		);
	};

	const formatDateTime = (dateString) => {
		if (!dateString) return "—";
		return new Date(new Date(dateString).getTime() - 4 * 60 * 60 * 1000).toLocaleString("en-US", {
			timeZone: "America/New_York",
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "numeric",
			minute: "numeric",
			hour12: true,
		});
	};

	const columns = [
		{
			key: "account",
			header: "Account",
			width: "lg",
		},
		{
			key: "value",
			header: "Value",
			width: "md",
			render: (value) => formatCurrency(value),
		},
		{
			key: "date_edited",
			header: "Last Updated",
			width: "lg",
			render: (date) => formatDateTime(date),
		},
	];

	const additionalActions = (
		<>
			<Link to="/addAccountBalance" className="btn btn--primary">Add Single</Link>
			<Link to="/addAccountBalances/NL" className="btn btn--secondary">Add NL</Link>
			<Link to="/addAccountBalances/Erin" className="btn btn--secondary">Add Erin</Link>
		</>
	);

	return (
		<>
			<CrudListPage
				title="Account Balances"
				actions={additionalActions}
				variant="v4"
				icon={<FiCreditCard />}
			>
				<DataTable
					columns={columns}
					data={data}
					editLinkBase="/updateAccountBalance"
					onDelete={handleDelete}
					loading={loading}
					emptyMessage="No account balances found."
					rowHighlight={(row) => row.highlight === "x"}
					variant="v4"
					onRowClick={handleRowClick}
					selectedId={selectedItem?.id}
				/>
			</CrudListPage>

			<DetailPanel
				isOpen={!!selectedItem}
				onClose={() => setSelectedItem(null)}
				title={selectedItem?.account || ""}
				subtitle="Account Balance"
				sidebar={renderPanelSidebar()}
				footer={renderPanelFooter()}
			>
				{renderPanelContent()}
			</DetailPanel>
		</>
	);
};

export default AccountBalance;
